<template>
  <v-navigation-drawer v-model="drawer" :width="325">
    <v-list density="compact">
      <v-list-item link v-if="!authenticated" v-on:click="login()" prepend-icon="mdi-send">
        <v-list-item-title>{{ $t('sidebar.login') }}</v-list-item-title>
      </v-list-item>

      <v-list-item link v-if="authenticated" v-on:click="logout()" prepend-icon="mdi-logout-variant">
        <v-list-item-title>{{ $t('sidebar.logout') }}</v-list-item-title>
      </v-list-item>
    </v-list>

    <v-divider></v-divider>

    <v-list v-if="isAdmin" density="compact">
      <!-- SEARCH -->
      <v-list-item link to="/user/search" :title="$t('sidebar.user.search')" append-icon="mdi-magnify"></v-list-item>
    </v-list>

    <v-divider v-if="isAdmin"></v-divider>

    <v-list v-if="isAdmin || isSales || isAuthor || isManager" density="compact">
      <!-- ORDERS -->
      <v-list-group v-if="isAdmin || isSales" value="orders">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.orders.title')"></v-list-item>
        </template>

        <v-list-item
          link
          to="/admin/carts-management"
          :title="$t('sidebar.orders.cartsManagement')"
          append-icon="mdi-cart-outline"
        ></v-list-item>
        <v-list-item
          link
          to="/admin/orders-management"
          :title="$t('sidebar.orders.ordersManagement')"
          append-icon="mdi-cart-check"
        ></v-list-item>
      </v-list-group>

      <!-- CONTENT -->
      <v-list-group v-if="isAdmin || isAuthor" value="content">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.content.title')"></v-list-item>
        </template>

        <v-list-item
          link
          to="/admin/resource-management"
          :title="$t('sidebar.admin.resources')"
          append-icon="mdi-semantic-web"
        ></v-list-item>
        <v-list-item link to="/admin/filters-management" :title="$t('sidebar.admin.filters')" append-icon="mdi-filter"></v-list-item>
        <v-list-item
          link
          to="/admin/menu-management"
          :title="$t('sidebar.admin.menu-management')"
          append-icon="mdi-dots-horizontal"
        ></v-list-item>
        <v-list-item
          link
          v-if="isAdmin"
          to="/admin/resource-properties-management"
          :title="$t('sidebar.admin.resource-properties-management')"
          append-icon="mdi-hammer-wrench"
        ></v-list-item>
        <v-list-item
          link
          v-if="isAdmin"
          to="/admin/resource-permissions-management"
          :title="$t('sidebar.admin.resource-permissions-management')"
          append-icon="mdi-account-supervisor"
        ></v-list-item>
        <v-list-item
          link
          v-if="isAdmin"
          to="/admin/products-management"
          :title="$t('sidebar.admin.products')"
          append-icon="mdi-car"
        ></v-list-item>
        <v-list-item link v-if="isAdmin" to="/admin/upload-file" :title="$t('sidebar.admin.upload')" append-icon="mdi-upload"></v-list-item>
      </v-list-group>

      <!-- PARTS -->
      <v-list-group v-if="isAdmin" value="parts">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.parts.title')"></v-list-item>
        </template>

        <v-list-item
          link
          to="/admin/parts-management"
          :title="$t('sidebar.admin.parts')"
          append-icon="mdi-hammer-wrench"
        ></v-list-item>
        <v-list-item
          link
          to="/admin/parts-upload"
          :title="$t('sidebar.admin.partsUpload')"
          append-icon="mdi-upload"
        ></v-list-item>
      </v-list-group>

      <!-- PRODUCTION -->
      <v-list-group v-if="isAdmin" value="production">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.production.title')"></v-list-item>
        </template>

        <v-list-item
          link
          to="/admin/production-import"
          :title="$t('sidebar.admin.productionImport')"
          append-icon="mdi-database-import"
        ></v-list-item>
        <v-list-item
          link
          to="/admin/production-units"
          :title="$t('sidebar.admin.productionUnits')"
          append-icon="mdi-car-multiple"
        ></v-list-item>
      </v-list-group>

      <!-- USERS -->
      <v-list-group v-if="isAdmin || isManager" value="users">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.users.title')"></v-list-item>
        </template>

        <v-list-item link to="/admin/user-management" :title="$t('sidebar.admin.users')" append-icon="mdi-account-supervisor"></v-list-item>
        <v-list-item
          link
          to="/admin/organizations-management"
          :title="$t('sidebar.admin.organizations')"
          append-icon="mdi-account-supervisor"
        ></v-list-item>
        <v-list-item
          link
          to="/admin/user-resource-permission-groups"
          :title="$t('sidebar.admin.user-resource-permission-groups')"
          append-icon="mdi-account-group"
        ></v-list-item>
      </v-list-group>

      <!-- EMAILS -->
      <v-list-group v-if="isAdmin || isSales || isManager" value="emails">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.emails.title')"></v-list-item>
        </template>

        <v-list-item
          link
          v-if="isAdmin || isSales"
          to="/admin/email-history"
          :title="$t('sidebar.emails.emailHistory')"
          append-icon="mdi-email-multiple-outline"
        ></v-list-item>
        <v-list-item
          link
          v-if="isAdmin || isManager"
          to="/admin/email-templates-management"
          :title="$t('sidebar.emails.emailTemplates')"
          append-icon="mdi-email-edit-outline"
        ></v-list-item>
      </v-list-group>

      <!-- OTHER -->
      <v-list-group v-if="isAdmin || isManager || isAuthor" value="other">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" :title="$t('sidebar.other.title')"></v-list-item>
        </template>

        <v-list-item
          link
          v-if="isAdmin"
          to="/admin/import-property-mapping"
          :title="$t('sidebar.admin.import-property-mapping')"
          append-icon="mdi-swap-horizontal"
        ></v-list-item>
        <v-list-item
          link
          v-if="isAdmin"
          to="/admin/import-auto-match-config"
          :title="$t('sidebar.admin.import-auto-match-config')"
          append-icon="mdi-auto-fix"
        ></v-list-item>
      </v-list-group>
    </v-list>

    <v-divider></v-divider>

    <v-list v-if="isUser" density="compact">
      <v-list-item link to="/user/search" :title="$t('sidebar.user.search')" prepend-icon="mdi-magnify"></v-list-item>
      <v-list-item link to="/user/orders" :title="$t('sidebar.user.orders')" prepend-icon="mdi-cart-check"></v-list-item>
    </v-list>

    <v-divider v-if="isUser"></v-divider>

    <user-menu-sidebar v-if="isUser" :root-menu-id="userRootMenuId" @openMenuContent="onOpenMenuContent" />

    <v-divider></v-divider>
    <div style="height: 64px"></div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, inject, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import LoginService from '@/account/login.service';
import AccountService from '@/account/account.service';
import TranslationService from '@/locale/translation.service';
import { type MenuId } from '@/venit/services/model/techpubs-cms';
import UserMenuSidebar from '@/venit/components/menu/user-menu-sidebar.vue';
import MenuUtils from '@/shared/menu/menu-utils.service';
import { useTranslationStore } from '@/stores/translation';
import { useAccountStore } from '@/stores/account';
import { useAppStateStore } from '@/stores/appState';

const router = useRouter();
const route = useRoute();
const loginService = inject<() => LoginService>('loginService')!;
const translationService = inject<() => TranslationService>('translationService')!;
const accountService = inject<() => AccountService>('accountService')!;

const translationStore = useTranslationStore();
const accountStore = useAccountStore();
const appStateStore = useAppStateStore();

const userRootMenuId = MenuUtils.userRootMenuId();
const currentUserMenuId = ref<MenuId | null>(null);

const currentLanguage = computed(() => translationStore.currentLanguage);
const authenticated = computed(() => accountStore.authenticated);
const isAdmin = computed(() => accountService().isAdmin);
const isSales = computed(() => accountService().isSales);
const isAuthor = computed(() => accountService().isAuthor);
const isManager = computed(() => accountService().isManager);
const isUser = computed(() => accountService().isUser);
const accountSwitched = computed(() => accountStore.accountSwitched);
const drawer = computed({
  get: () => appStateStore.drawer,
  set: (value: boolean) => appStateStore.setDrawer(value),
});

const logout = (): void => {
  if (accountSwitched.value) {
    loginService().exitImpersonate();
  } else {
    loginService().logout();
  }
};

const login = (): void => {
  router.push('/login').catch(() => {});
};

const onOpenMenuContent = (menuId: MenuId): void => {
  currentUserMenuId.value = menuId;
  const destPath = '/user/page/' + menuId.id;
  if (route.path != destPath) {
    router.push(destPath).catch(error => {
      if (error.name != 'NavigationDuplicated') {
        throw error;
      }
    });
  }
};

onMounted(() => {
  translationService().refreshTranslation(currentLanguage.value);
});
</script>
